import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  BriefcaseBusiness, Loader2, Save, CheckCircle2, Lock, Plus, Trash2,
  Calendar, Users, FileText, TrendingUp, History, Unlock, LayoutDashboard, Flame,
} from 'lucide-react';
import { toast } from 'sonner';
import { DeclDashboard } from '../components/bb/DeclDashboard';
import DirectionView from '../components/bb/DirectionView';
import PointsIndividuels from '../components/pi/PointsIndividuels';
import MonBusiness from '../components/bb/MonBusiness';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const COMMERCIAL_FIELDS = [
  { key: 'rdv_s_minus_1', label: 'Nombre de RDV S-1' },
  { key: 'rdv_current', label: 'Nombre de RDV semaine en cours' },
  { key: 'contrats_signes_s_minus_1', label: 'Nombre de contrats signés S-1' },
  { key: 'contrats_projetes_semaine', label: 'Nombre de contrats projetés semaine S', triggersDetail: 'projection_semaine' },
  { key: 'contrats_projetes_mois', label: 'Nombre de contrats projetés mois M', triggersDetail: 'projection_mois' },
  { key: 'ouvertures_prospects_mois', label: 'Nombre d\u2019ouvertures prospects sur le mois M' },
  { key: 'ouvertures_renouvellements_mois', label: 'Nombre d\u2019ouvertures renouvellements sur le mois M' },
];

const ASSISTANT_FIELDS = [
  { key: 'appels_realises', label: 'Nombre d\u2019appels r\u00e9alis\u00e9s S-1' },
  { key: 'rdv_pris', label: 'Nombre de RDV pris' },
  { key: 'contrats_edites', label: 'Nombre de contrats \u00e9dit\u00e9s' },
  { key: 'contrats_renouvellements_signes', label: 'Nombre de contrats renouvellements sign\u00e9s', triggersDetail: 'renouvellement' },
  { key: 'contrats_prospects_signes', label: 'Nombre de contrats prospects sign\u00e9s', triggersDetail: 'prospect' },
];

// ISO Monday->Friday date range for current week
function weekDateRange(year, week) {
  if (!year || !week) return '';
  // Compute ISO Monday from year/week
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfJan4 = jan4.getUTCDay() || 7; // 1..7
  const mondayWeek1 = new Date(jan4);
  mondayWeek1.setUTCDate(jan4.getUTCDate() - dayOfJan4 + 1);
  const monday = new Date(mondayWeek1);
  monday.setUTCDate(mondayWeek1.getUTCDate() + (week - 1) * 7);
  const friday = new Date(monday);
  friday.setUTCDate(monday.getUTCDate() + 4);
  const f = (d) => `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  return `Du lundi ${f(monday)} au vendredi ${f(friday)}`;
}

const DETAIL_LABELS = {
  projection_semaine: 'D\u00e9tails projection semaine S (ultra chaud)',
  projection_mois: 'D\u00e9tails projection mois M (ultra chaud)',
  renouvellement: 'D\u00e9tails contrats renouvellements sign\u00e9s',
  prospect: 'D\u00e9tails contrats prospects sign\u00e9s',
};

export default function BusinessCommercialPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  // business_profile (assigné par un admin) prend le pas sur le rôle pour ce module.
  // Permet à un super_admin (ex. Eunice) de saisir en tant qu'assistante.
  const effectiveProfile =
    user?.business_profile === 'commercial' || user?.business_profile === 'assistant'
      ? user.business_profile
      : (['commercial', 'commerciale'].includes(user?.role)
          ? 'commercial'
          : (user?.role === 'assistante_commerciale' ? 'assistant' : null));
  const isCommercial = effectiveProfile === 'commercial';
  const isAssistant = effectiveProfile === 'assistant';
  // Un admin qui a aussi un business_profile doit pouvoir saisir SA fiche ET voir le Dashboard Direction.
  const hasOwnFiche = !!effectiveProfile;

  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [totalRdvPris, setTotalRdvPris] = useState(0);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form local state
  const [form, setForm] = useState({});
  const [comments, setComments] = useState('');
  const [details, setDetails] = useState([]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const refresh = async () => {
    setLoading(true);
    try {
      const [curRes, listRes] = await Promise.all([
        fetch(`${API}/api/business-reports/current`, { headers }),
        fetch(`${API}/api/business-reports`, { headers }),
      ]);
      const cur = curRes.ok ? await curRes.json() : null;
      const list = listRes.ok ? await listRes.json() : [];
      setCurrent(cur);
      setHistory(list);
      // Calc total RDV pris assistante (year)
      if (isAssistant) {
        const total = list
          .filter(r => r.role_type === 'assistant' && r.year === (cur?.year || new Date().getFullYear()))
          .reduce((s, r) => s + (r.data_json?.rdv_pris || 0), 0);
        setTotalRdvPris(total);
      }
      if (cur && cur.id) {
        setForm(cur.data_json || {});
        setComments(cur.comments || '');
        setDetails(cur.details || []);
      }
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) refresh(); }, [token]);

  const isLocked = current?.status === 'validated' && !isAdmin;
  const fields = isAssistant ? ASSISTANT_FIELDS : COMMERCIAL_FIELDS;
  const isFormVisible = current?.id; // admin without fiche won't see form

  const save = async (alsoValidate = false) => {
    if (!current?.id) return;
    if (alsoValidate) setValidating(true); else setSaving(true);
    try {
      const body = { ...form, comments, details };
      const res = await fetch(`${API}/api/business-reports/${current.id}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
      toast.success('Brouillon enregistré');
      if (alsoValidate) {
        const r2 = await fetch(`${API}/api/business-reports/${current.id}/validate`, { method: 'POST', headers });
        if (r2.ok) toast.success('Fiche validée — admins notifiés');
      }
      await refresh();
    } finally { setSaving(false); setValidating(false); }
  };

  const reopen = async () => {
    if (!current?.id) return;
    const r = await fetch(`${API}/api/business-reports/${current.id}/reopen`, { method: 'POST', headers });
    if (r.ok) { toast.success('Fiche réouverte'); refresh(); }
  };

  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v === '' ? '' : Number(v) }));

  // Detail rows management
  const addDetail = (type) => setDetails(d => [...d, {
    detail_type: type, enseigne: '', ville: '', ca_previsionnel: '', taux_commission: '',
    date_signature_estimee: '', commentaire: '',
  }]);
  const removeDetail = (idx) => setDetails(d => d.filter((_, i) => i !== idx));
  const updateDetail = (idx, k, v) => setDetails(d => d.map((x, i) => i === idx ? { ...x, [k]: v } : x));

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="business-commercial-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <BriefcaseBusiness className="w-7 h-7 text-indigo-500" />
              Business Commercial
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Suivi hebdomadaire {isAssistant ? '— Assistante commerciale' : isCommercial ? '— Commercial(e)' : '— Vue Direction'}
            </p>
          </div>
          {current?.year && (
            <Badge variant="outline" className="text-sm gap-1 px-3 py-1" data-testid="week-range-badge">
              <Calendar className="w-3.5 h-3.5" />
              {weekDateRange(current.year, current.week_number)}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : (
          <Tabs defaultValue={['dashboard', 'points'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'hot'} className="space-y-4">
            <TabsList className="bg-slate-100 p-1 rounded-lg max-w-full overflow-x-auto justify-start [&>button]:shrink-0">
              <TabsTrigger value="hot" className="gap-1" data-testid="tab-hot-business">
                <Flame className="w-4 h-4 text-orange-500" /> {isAdmin ? 'Business Commercial' : 'Mon business'}
              </TabsTrigger>
              <TabsTrigger value="dashboard" className="gap-1" data-testid="tab-dashboard">
                <LayoutDashboard className="w-4 h-4" /> Dashboard consolidé
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="points" className="gap-1" data-testid="tab-points">
                  <LayoutDashboard className="w-4 h-4 text-orange-500" /> Points individuels
                </TabsTrigger>
              )}
              {isFormVisible && (
                <TabsTrigger value="history" className="gap-1" data-testid="tab-history">
                  <History className="w-4 h-4" /> Historique
                </TabsTrigger>
              )}
            </TabsList>

            {isFormVisible && (
              <TabsContent value="fiche" className="space-y-4 mt-0">
                {/* Form Card */}
              <Card className={`border-l-4 ${isLocked ? 'border-l-emerald-500 bg-emerald-50/30' : 'border-l-indigo-500'}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-indigo-500" />
                      Fiche hebdomadaire — {weekDateRange(current.year, current.week_number)}
                    </CardTitle>
                    {current.status === 'validated' && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1">
                        <Lock className="w-3 h-3" /> Validée
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Total RDV pris (assistante seulement) */}
                  {isAssistant && (
                    <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Total RDV pris (année)</p>
                        <p className="text-xs text-indigo-600">Cumul automatique des fiches validées + brouillons depuis le 01/01</p>
                      </div>
                      <p className="text-3xl font-bold text-indigo-700">{totalRdvPris}</p>
                    </div>
                  )}

                  {/* Numeric grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {fields.map(f => (
                      <div key={f.key}>
                        <Label className="text-xs">{f.label}</Label>
                        <Input
                          type="number" min="0"
                          value={form[f.key] ?? 0}
                          onChange={(e) => updateField(f.key, e.target.value)}
                          disabled={isLocked}
                          data-testid={`field-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Détails — affichés UNIQUEMENT si une projection/contrat signé > 0 est déclaré */}
                  {(() => {
                    // Compute which detail types should be visible based on declared counts
                    const activeDetailTypes = fields
                      .filter(f => f.triggersDetail && Number(form[f.key]) > 0)
                      .map(f => f.triggersDetail);

                    if (activeDetailTypes.length === 0) {
                      return (
                        <div className="text-xs text-slate-400 italic px-3 py-2 bg-slate-50 rounded border border-dashed border-slate-200">
                          {isAssistant
                            ? 'Aucun contrat signé déclaré — la section "Détails" apparaîtra dès que tu saisiras au moins 1 renouvellement ou 1 prospect signé.'
                            : 'Aucune projection en cours — la section "Détails" apparaîtra dès que tu saisiras au moins 1 contrat projeté en semaine S ou en mois M.'}
                        </div>
                      );
                    }

                    return activeDetailTypes.map(typeKey => {
                      const detailsOfType = details.filter(d => d.detail_type === typeKey);
                      const expected = Number(form[fields.find(f => f.triggersDetail === typeKey)?.key] || 0);
                      return (
                        <div key={typeKey} className="space-y-2 border-l-4 border-l-orange-400 bg-orange-50/30 p-3 rounded-r">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <p className="text-sm font-semibold text-slate-800">
                              {DETAIL_LABELS[typeKey]}
                              <span className="ml-2 text-xs font-normal text-orange-600">
                                ({detailsOfType.length} / {expected} renseigné{expected > 1 ? 's' : ''})
                              </span>
                            </p>
                            {!isLocked && (
                              <Button size="sm" variant="outline" onClick={() => addDetail(typeKey)}
                                className="h-8 text-xs gap-1 border-orange-300 text-orange-700"
                                data-testid={`add-detail-${typeKey}`}>
                                <Plus className="w-3 h-3" /> Ajouter
                              </Button>
                            )}
                          </div>
                          {detailsOfType.length === 0 ? (
                            <p className="text-xs text-orange-700 italic">Clique sur "Ajouter" pour saisir les {expected} contrat{expected > 1 ? 's' : ''} concerné{expected > 1 ? 's' : ''}</p>
                          ) : (
                            <div className="space-y-2">
                              {detailsOfType.map((d) => {
                                const idx = details.indexOf(d);
                                return (
                                  <div key={idx} className="border rounded-md p-2 bg-white">
                                    <div className="flex items-center justify-end mb-1">
                                      {!isLocked && (
                                        <Button size="sm" variant="ghost" onClick={() => removeDetail(idx)}
                                          className="h-7 px-2 text-red-500">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      <Input value={d.enseigne || ''} disabled={isLocked}
                                        onChange={(e) => updateDetail(idx, 'enseigne', e.target.value)}
                                        placeholder="Enseigne" className="h-9 text-xs" />
                                      <Input value={d.ville || ''} disabled={isLocked}
                                        onChange={(e) => updateDetail(idx, 'ville', e.target.value)}
                                        placeholder="Ville" className="h-9 text-xs" />
                                      <Input type="number" value={d.ca_previsionnel || ''} disabled={isLocked}
                                        onChange={(e) => updateDetail(idx, 'ca_previsionnel', e.target.value ? Number(e.target.value) : null)}
                                        placeholder="CA prévisionnel annuel HT (€)" className="h-9 text-xs" />
                                      <Input type="number" step="0.1" value={d.taux_commission || ''} disabled={isLocked}
                                        onChange={(e) => updateDetail(idx, 'taux_commission', e.target.value ? Number(e.target.value) : null)}
                                        placeholder="Taux commission %" className="h-9 text-xs" />
                                      {(typeKey === 'projection_semaine' || typeKey === 'projection_mois') && (
                                        <Input type="date" value={d.date_signature_estimee || ''} disabled={isLocked}
                                          onChange={(e) => updateDetail(idx, 'date_signature_estimee', e.target.value)}
                                          className="h-9 text-xs" placeholder="Date estimée signature" />
                                      )}
                                      <Input value={d.commentaire || ''} disabled={isLocked}
                                        onChange={(e) => updateDetail(idx, 'commentaire', e.target.value)}
                                        placeholder="Commentaire" className="h-9 text-xs col-span-2 sm:col-span-4" />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}

                  {/* Commentaires */}
                  <div>
                    <Label className="text-xs">Commentaires</Label>
                    <textarea value={comments} onChange={(e) => setComments(e.target.value)}
                      disabled={isLocked} placeholder="Notes libres pour le point business…"
                      className="w-full border rounded-md p-2 text-sm min-h-[80px] disabled:bg-slate-50"
                      data-testid="field-comments" />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t">
                    {isLocked && isAdmin && (
                      <Button variant="outline" onClick={reopen} className="gap-1" data-testid="reopen-btn">
                        <Unlock className="w-4 h-4" /> Réouvrir (admin)
                      </Button>
                    )}
                    {!isLocked && (
                      <>
                        <Button variant="outline" onClick={() => save(false)} disabled={saving}
                          className="gap-1" data-testid="save-draft-btn">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Enregistrer brouillon
                        </Button>
                        <Button onClick={() => save(true)} disabled={validating}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                          data-testid="validate-btn">
                          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                          Valider définitivement
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
              </TabsContent>
            )}

            {!isFormVisible && (
              <TabsContent value="dashboard" className="mt-0">
                <DeclDashboard token={token} isAdmin={isAdmin} />
              </TabsContent>
            )}
            {isFormVisible && (
              <TabsContent value="dashboard" className="mt-0">
                <DeclDashboard token={token} isAdmin={isAdmin} />
              </TabsContent>
            )}

            <TabsContent value="hot" className="mt-0">
              {isAdmin ? <DirectionView token={token} user={user} /> : <MonBusiness token={token} />}
            </TabsContent>
            {isAdmin && (
              <TabsContent value="points" className="mt-0">
                <PointsIndividuels token={token} />
              </TabsContent>
            )}

            {isFormVisible && (
              <TabsContent value="history" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <History className="w-5 h-5 text-slate-500" /> Historique des fiches
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {history.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">Aucune fiche</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 uppercase border-b">
                              <th className="py-2 pr-2">Semaine</th>
                              <th className="py-2 pr-2">Profil</th>
                              {isAdmin && <th className="py-2 pr-2">Utilisateur</th>}
                              <th className="py-2 pr-2">Statut</th>
                              <th className="py-2 pr-2">Validée le</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.map(r => (
                              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`history-row-${r.id}`}>
                                <td className="py-2 pr-2 font-semibold">S{r.week_number}/{r.year}</td>
                                <td className="py-2 pr-2">
                                  <Badge variant="outline" className="text-[10px]">
                                    {r.role_type === 'commercial' ? 'Commercial' : 'Assistante'}
                                  </Badge>
                                </td>
                                {isAdmin && <td className="py-2 pr-2 text-xs">{r.user_name || r.user_email}</td>}
                                <td className="py-2 pr-2">
                                  {r.status === 'validated' ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] gap-1">
                                      <CheckCircle2 className="w-3 h-3" /> Validée
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px]">Brouillon</Badge>
                                  )}
                                </td>
                                <td className="py-2 pr-2 text-xs text-slate-500">
                                  {r.validated_at ? new Date(r.validated_at).toLocaleDateString('fr-FR') : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </AppLayout>
  );
}
